import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined';
import { Button, Col, Input, InputRef, Row } from 'antd';
import Form, { FormItemProps } from 'antd/lib/form';
import { InputProps } from 'antd/lib/input';
import { ChangeEvent, ForwardedRef, forwardRef, JSX } from 'react';
import { useDebounce } from 'use-debounce';

import {
  CountryCode,
  getCellPhoneValidationError,
  PhoneSupportedCountryCodes,
  validateCellPhone,
} from '../../../utils/phone';
import { Dropdown } from '../../Dropdown';

export interface PhoneInputProps {
  phoneNumber: string;
  countryCode: CountryCode;
  errorMessage: string;
  /** Shown instead of errorMessage when the number is well-formed but from a country we don't accept */
  unsupportedCountryErrorMessage?: string;
  phoneSupportedCountryCodes: PhoneSupportedCountryCodes[];
  placeholder?: string;
  setPhoneNumber: (value: string) => void;
  setCountryCode: (value: CountryCode) => void;
  validator?: (cellPhone: string, countryCode: CountryCode) => boolean;
  formItemProps: FormItemProps;
  useNewLayout?: boolean;
}

export const PhoneInput = forwardRef(
  (
    {
      countryCode,
      errorMessage,
      unsupportedCountryErrorMessage,
      formItemProps,
      phoneNumber,
      phoneSupportedCountryCodes,
      placeholder,
      setPhoneNumber,
      setCountryCode,
      validator = validateCellPhone,
      size,
      useNewLayout = false,
      ...otherProps
    }: PhoneInputProps & InputProps,
    ref: ForwardedRef<InputRef>,
  ): JSX.Element => {
    const [debouncedPhone] = useDebounce<string>(phoneNumber, 1000);

    let validateStatus: 'error' | 'success' | undefined;
    let help;

    if (debouncedPhone.length > 0) {
      if (validator?.(debouncedPhone, countryCode)) {
        validateStatus = 'success';
      } else {
        validateStatus = 'error';
        help =
          unsupportedCountryErrorMessage &&
          getCellPhoneValidationError(debouncedPhone, countryCode) === 'unsupported_country'
            ? unsupportedCountryErrorMessage
            : errorMessage;
      }
    }
    const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(e.target.value);
    };

    const handleCountryCodeChange = (e: { key: string | number }) => {
      const index = typeof e.key === 'number' ? e.key : parseInt(e.key, 10);
      setCountryCode(phoneSupportedCountryCodes[index].isoCode);
    };

    const menu = phoneSupportedCountryCodes.map(
      (cc: PhoneSupportedCountryCodes, index: number) => ({
        key: index.toString(),
        children: cc.name,
      }),
    );

    return (
      <Form.Item
        hasFeedback={!!(formItemProps.validateStatus ?? validateStatus)}
        validateStatus={formItemProps.validateStatus ?? validateStatus}
        help={formItemProps.help ?? help}
        htmlFor="cellPhone"
        name="cellPhone"
        labelCol={{ span: 24 }}
        {...formItemProps}
      >
        {useNewLayout ? (
          <Row gutter={2}>
            <Col flex="85px">
              <Dropdown options={menu} menuProps={{ onClick: handleCountryCodeChange }}>
                <Button size={size ?? 'large'}>
                  {countryCode} <CaretDownOutlined />
                </Button>
              </Dropdown>
            </Col>
            <Col flex="auto">
              <Input
                size={size ?? 'large'}
                onChange={handlePhoneNumberChange}
                placeholder={placeholder}
                value={phoneNumber}
                id="cellPhone"
                ref={ref}
                {...otherProps}
              />
            </Col>
          </Row>
        ) : (
          <Row gutter={8}>
            <Col span={20}>
              <Input
                size={size ?? 'large'}
                onChange={handlePhoneNumberChange}
                placeholder={placeholder}
                value={phoneNumber}
                id="cellPhone"
                ref={ref}
                {...otherProps}
              />
            </Col>
            <Col span={4}>
              <Dropdown options={menu} menuProps={{ onClick: handleCountryCodeChange }}>
                <Button size={size ?? 'large'}>
                  {countryCode} <CaretDownOutlined />
                </Button>
              </Dropdown>
            </Col>
          </Row>
        )}
      </Form.Item>
    );
  },
);
